import { useContext, useEffect, useMemo, useState } from 'react';
import { BanksContext } from '../../context/bank/BankContext';
import { Bank } from '../../models/interfaces';
import Message from '../../components/ui/Message';
import FormModal from '../../components/ui/FormModal';
import { useRequiredFields } from '../../hooks/useRequiredFields';
import ApiClientsModal from './ApiClientsModal';

const EMPTY: Partial<Bank> = { name: '', email: '', phone: '', status: 'ACT', countryId: undefined };
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Bancos: los administra la plataforma. Los bancos se desactivan (no se borran) y solo si no tienen comercios activos.
export const BanksPage = () => {
    const { banks, countries, isLoading, loadBanks, saveBank, deleteBank } = useContext(BanksContext);
    const [search, setSearch] = useState('');
    const [countryFilter, setCountryFilter] = useState<number | ''>('');
    const [editing, setEditing] = useState<Partial<Bank> | null>(null);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');
    const [error, setError] = useState('');
    const [toDeactivate, setToDeactivate] = useState<Bank | null>(null);
    const [integrationsOf, setIntegrationsOf] = useState<Bank | null>(null);
    const required = useRequiredFields();

    useEffect(() => { loadBanks(); }, [loadBanks]);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        const byCountry = countryFilter ? banks.filter(bank => bank.countryId === countryFilter) : banks;
        if (!term) return byCountry;
        return byCountry.filter(bank => [bank.name, bank.email, bank.phone, bank.countryName].some(v => String(v ?? '').toLowerCase().includes(term)));
    }, [banks, search, countryFilter]);

    const openForm = (bank?: Bank) => {
        required.reset();
        setFormError('');
        // País por defecto: el del filtro, o el único que haya
        setEditing(bank ? { ...bank } : { ...EMPTY, countryId: countryFilter || (countries.length === 1 ? countries[0].id : undefined) });
    };

    const update = (field: keyof Bank, value: string | number | undefined) => setEditing(prev => ({ ...prev, [field]: value }));

    const emailInvalid = !!editing?.email && !EMAIL_REGEX.test(editing.email);

    const handleSave = async () => {
        if (!editing) return;
        if (!editing.name?.trim() || !editing.countryId || emailInvalid) {
            required.fail();
            setFormError(emailInvalid ? 'El correo no es válido.' : 'Debe completar los campos requeridos.');
            return;
        }
        setSaving(true);
        const resp = await saveBank(editing);
        setSaving(false);
        if (resp === 'success') setEditing(null);
        else setFormError(resp);
    };

    const processDeactivate = async () => {
        const bank = toDeactivate;
        setToDeactivate(null);
        if (!bank) return;
        const resp = await deleteBank(bank.id);
        if (resp !== 'success') setError(resp);
    };

    return (
        <div className='mt-5 container-fluid px-4' style={{ flex: 1, marginTop: 10 }}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Bancos</h3>
                    <p className="text-muted mb-0">Bancos afiliadores por país: cada comercio y cada usuario de banco pertenece a uno.</p>
                </div>
                <button type="button" className="btn btn-primary mt-2 mt-md-0" onClick={() => openForm()}>
                    <i className="bi bi-plus-lg me-1" /> Agregar banco
                </button>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body">
                    <div className="row g-2 mb-3">
                        <div className="col-md-6">
                            <div className="input-group">
                                <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                                <input className="form-control" type="text" placeholder="Buscar por nombre, correo o teléfono..."
                                    value={search} onChange={(e) => setSearch(e.target.value)} />
                                {search && (
                                    <button className="btn btn-outline-secondary" type="button" title="Limpiar búsqueda" onClick={() => setSearch('')}>
                                        <i className="bi bi-x-lg" />
                                    </button>
                                )}
                            </div>
                        </div>
                        {countries.length > 1 && (
                            <div className="col-md-3">
                                <select className="form-select" aria-label="País" value={countryFilter}
                                    onChange={(e) => setCountryFilter(e.target.value ? Number(e.target.value) : '')}>
                                    <option value="">Todos los países</option>
                                    {countries.map(country => <option key={country.id} value={country.id}>{country.name}</option>)}
                                </select>
                            </div>
                        )}
                    </div>

                    <div className='table-responsive'>
                        <table className="table table-hover align-middle">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th>Banco</th>
                                    <th>País</th>
                                    <th>Correo</th>
                                    <th>Teléfono</th>
                                    <th>Estado</th>
                                    <th style={{ textAlign: 'center' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan={6} className='text-center py-4'>
                                        <span className="spinner-border spinner-border-sm me-2" /> Cargando...
                                    </td></tr>
                                ) : filtered.length > 0 ? filtered.map(bank => (
                                    <tr key={bank.id}>
                                        <td className="fw-medium"><i className="bi bi-bank me-2 text-muted" />{bank.name}</td>
                                        <td>{bank.countryName ?? '-'}</td>
                                        <td>{bank.email || '-'}</td>
                                        <td>{bank.phone || '-'}</td>
                                        <td>
                                            {bank.status === 'INA'
                                                ? <span className="badge rounded-pill bg-secondary-subtle text-secondary-emphasis">Inactivo</span>
                                                : <span className="badge rounded-pill bg-success-subtle text-success-emphasis">Activo</span>}
                                        </td>
                                        <td style={{ textAlign: 'center', width: 150, whiteSpace: 'nowrap' }}>
                                            <button type="button" className="btn btn-sm btn-primary" title="Editar banco" onClick={() => openForm(bank)}>
                                                <i className="bi bi-pencil" />
                                            </button>
                                            <button type="button" className="btn btn-sm btn-outline-secondary ms-1" title="Integraciones"
                                                aria-label={`Integraciones de ${bank.name}`} onClick={() => setIntegrationsOf(bank)}>
                                                <i className="bi bi-plug" />
                                            </button>
                                            {bank.status !== 'INA' && (
                                                <button type="button" className="btn btn-sm btn-danger ms-1" title="Desactivar banco" onClick={() => setToDeactivate(bank)}>
                                                    <i className="bi bi-slash-circle" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan={6} className='text-center py-4 text-muted'>
                                        {search ? 'No se encontraron bancos para la búsqueda.' : 'No existen bancos.'}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div style={{ height: '60px' }}></div>

            {editing && (
                <FormModal
                    title={editing.id ? 'Editar banco' : 'Agregar banco'}
                    onClose={() => setEditing(null)}
                    onSave={handleSave}
                    saving={saving}
                    error={formError}
                >
                    <div className="row g-3 mb-3">
                        <div className="col-md-7">
                            <label htmlFor="bankName" className="form-label">Nombre *</label>
                            <input id="bankName" className={`form-control${required.mark(!editing.name?.trim())}`} maxLength={50} autoFocus
                                value={editing.name ?? ''} onChange={(e) => update('name', e.target.value)} />
                            <div className="invalid-feedback">Campo requerido</div>
                        </div>
                        <div className="col-md-5">
                            <label htmlFor="bankCountry" className="form-label">País *</label>
                            <select id="bankCountry" className={`form-select${required.mark(!editing.countryId)}`} value={editing.countryId ?? ''}
                                onChange={(e) => update('countryId', e.target.value ? Number(e.target.value) : undefined)}>
                                <option value="">Seleccione el país</option>
                                {countries.map(country => <option key={country.id} value={country.id}>{country.name}</option>)}
                            </select>
                            <div className="invalid-feedback">Campo requerido</div>
                            {editing.id && <div className="form-text">No se puede cambiar si el banco tiene comercios.</div>}
                        </div>
                    </div>
                    <div className="row g-3">
                        <div className="col-md-7">
                            <label htmlFor="bankEmail" className="form-label">Correo de contacto</label>
                            <input id="bankEmail" type="email" className={`form-control${required.mark(emailInvalid)}`} maxLength={100}
                                value={editing.email ?? ''} onChange={(e) => update('email', e.target.value)} />
                            <div className="invalid-feedback">Ingrese un correo válido</div>
                        </div>
                        <div className="col-md-5">
                            <label htmlFor="bankPhone" className="form-label">Teléfono</label>
                            <input id="bankPhone" className="form-control" maxLength={30}
                                value={editing.phone ?? ''} onChange={(e) => update('phone', e.target.value)} />
                        </div>
                    </div>
                    {editing.id && (
                        <div className="mt-3">
                            <label htmlFor="bankStatus" className="form-label">Estado</label>
                            <select id="bankStatus" className="form-select" value={editing.status ?? 'ACT'} onChange={(e) => update('status', e.target.value)}>
                                <option value="ACT">Activo</option>
                                <option value="INA">Inactivo</option>
                            </select>
                            <div className="form-text">Solo se puede desactivar si no tiene comercios activos.</div>
                        </div>
                    )}
                </FormModal>
            )}

            {integrationsOf && <ApiClientsModal bank={integrationsOf} onClose={() => setIntegrationsOf(null)} />}

            {error && (
                <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)" />
            )}

            {toDeactivate && (
                <Message
                    type="success"
                    title="Atención"
                    message={`¿Desea desactivar el banco "${toDeactivate.name}"? Sus usuarios dejarán de poder administrarlo.`}
                    closeDialog={() => setToDeactivate(null)}
                    showCancel={true}
                    handleCancel={() => setToDeactivate(null)}
                    handleOk={processDeactivate}
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}
        </div>
    );
};
